import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="auth-shell">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1">{title}</h1>
      <p className="text-[var(--ink-2)] text-sm mb-8">{subtitle}</p>
      {children}
    </div>
  );
}
export { AuthShell };

const inputCls = "w-full px-4 py-3 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setUnverified(false); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      const dest = location.state?.from || (["provider_admin", "platform_admin", "tutor"].includes(u.role) ? "/provider" : "/dashboard");
      navigate(dest);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      if (err.response?.status === 403) setUnverified(true);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      toast.success("Verification link sent.");
      if (data.dev_verification_url) window.location.href = data.dev_verification_url;
    } catch { toast.error("Could not resend."); }
  };

  return (
    <AuthShell title="Log in" subtitle="Continue your learning journey.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input data-testid="login-email" type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        {error && <p className="text-sm text-[var(--destructive,#D50000)]" data-testid="login-error">{error}</p>}
        {unverified && <button type="button" onClick={resend} className="text-sm text-[var(--accent)] underline" data-testid="resend-verification">Resend verification email</button>}
        <button disabled={loading} data-testid="login-submit" className="w-full bg-[var(--accent)] text-white font-semibold py-3.5 hover:bg-[var(--accent-hover)] transition-colors disabled:opacity-60">{loading ? "Logging in…" : "Log in"}</button>
      </form>
      <div className="flex justify-between mt-5 text-sm">
        <Link to="/forgot-password" className="text-[var(--ink-2)] hover:text-[var(--ink)]" data-testid="forgot-link">Forgot password?</Link>
        <Link to="/register" className="font-semibold hover:text-[var(--accent)]" data-testid="to-register">Create account</Link>
      </div>
    </AuthShell>
  );
}
